import React, { cloneElement, useState } from "react";
import { Overlay } from "react-overlays";

import EventUtils from "../../utils/EventUtils";

function contains(context, node) {
  if (context.contains) return context.contains(node);
  if (context.compareDocumentPosition)
    return context === node || !!(context.compareDocumentPosition(node) & 16);
}

// Simple implementation of mouseEnter and mouseLeave.
// React's built version is broken: https://github.com/facebook/react/issues/4251
// for cases when the trigger is disabled and mouseOut/Over can cause flicker
// moving from one child element to another.
function handleMouseOverOut(e, relatedNative) {
  const target = e.currentTarget;
  const related = e.relatedTarget || e.nativeEvent[relatedNative];

  if ((!related || related !== target) && !contains(target, related)) {
    return true;
  }
  return false;
}

function OverlayTrigger({
  children,
  closeOverlayOnClick = false,
  offset,
  overlay: overlayElement,
  placement = "bottom",
  rootClose = true,
  trigger = "hover",
  triggerPassthrough = false,
}) {
  const [show, setShow] = useState(false);

  function _toggle(e) {
    e.preventDefault();

    if (triggerPassthrough) {
      e.stopPropagation();
      EventUtils.emitPassthroughClick();
    }

    setShow(!show);
  }

  const _hide = () => {
    setShow(false);
  };

  function _show() {
    setShow(true);
  }

  function _mouseOver(e) {
    if (handleMouseOverOut(e, "fromElement")) {
      _show();
    }
  }

  function _mouseOut(e) {
    if (handleMouseOverOut(e, "toElement")) {
      _hide();
    }
  }

  /**
   * Ref or html element that triggers the overlay, this tells popper.js what to position the overlay to
   * @param {Ref|HTMLElement} target
   * @returns
   */
  function _getOverlay(target) {
    const overlayProps = closeOverlayOnClick ? { onClick: _toggle } : {};

    return (
      <Overlay
        show={show}
        onHide={_hide}
        offset={offset}
        placement={placement}
        container={document.body}
        rootClose={rootClose}
        target={target.ref}
        flip
      >
        {({ props, arrowProps, placement: passedPlacement }) =>
          overlayElement &&
          cloneElement(overlayElement, {
            tooltipProps: props,
            arrowProps,
            placement: passedPlacement,
            ...overlayProps,
          })
        }
      </Overlay>
    );
  }

  const triggerProps = {};
  const child = React.Children.only(children);
  const overlay = _getOverlay(child);

  switch (trigger) {
    case "click":
      triggerProps.onClick = _toggle;
      break;
    case "hover":
      triggerProps.onMouseOver = _mouseOver;
      triggerProps.onMouseOut = _mouseOut;
      break;
    case "focus":
      triggerProps.onFocus = _show;
      triggerProps.onBlur = _hide;
      break;
    default:
  }

  // the triggering element
  const element = cloneElement(child, triggerProps);
  return (
    <>
      {element}
      {overlay}
    </>
  );
}

export default OverlayTrigger;
